import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import ExternalCheckout from './PaymentFlows/ExternalCheckout/ExternalCheckout';
import IntegratedPOS from './PaymentFlows/IntegratedPOS/IntegratedPOS';
import Signup from './PaymentFlows/Signup/Signup';
import ShowPrint from './PaymentFlows/ShowPrint/ShowPrint';
import DynamicForm from './PaymentSteps/DynamicForm/DynamicForm';
import DataError from '../DataError/DataError';
import DataLoader from '../DataLoader/DataLoader';
import Activity from '../../helpers/Activity';
import Debug from '../../helpers/Debug';
import WebServices from '../../helpers/WebServices';
import { firestore, getCollectionPath } from '../../config/Firebase';
import './PaymentModal.css';

function PaymentModal({
    instance,
    client,
    store,
    shoppingCart,
    paymentMode,
    pk,
    taxMultiplier,
    taxLabel,
    mainClientLogo,
    kioskOptionImage,
    smartphoneOptionImage,
    smartphoneOptionQrScanImage,
    personOptionImage,
    companyOptionImage,
    cardOptionImage,
    otherPaymentOptionImage,
    ticketOptionImage,
    invoiceOptionImage,
    timerImage,
    printMessageImage,
    paymentSuccessImage,
    getHelpErrorImage,
    actionButtonBackgroundColor,
    actionButtonBorderColor,
    actionButtonLabelColor,
    cancelButtonBackgroundColor,
    cancelButtonBorderColor,
    cancelButtonLabelColor,
    backButtonBackgroundColor,
    backButtonBorderColor,
    backButtonLabelColor,
    customInputOutline,
    customInputClearButtonBackgroundColor,
    customInputClearButtonLabelColor,
    inSmallMode,
    openShoppingCart,
    resetIdleModeManagerTimer,
    allowClosing,
    preventClosing,
    flowFinished,
    handleClose,
    silentClose,
    metadata
}) {
    const [isLoadingCheckout, setIsLoadingCheckout] = useState(true);
    const [checkoutLoaded, setCheckoutLoaded] = useState(false);
    const [checkoutUrl, setCheckoutUrl] = useState(undefined);
    const [clientDocumentUrl, setClientDocumentUrl] = useState(undefined);
    const [orderNumber, setOrderNumber] = useState(undefined);
    const [paymentNumber, setPaymentNumber] = useState(undefined);
    const [error, setError] = useState(undefined);
    const [isLoadingDynamicForm, setIsLoadingDynamicForm] = useState(true);
    const [dynamicForm, setDynamicForm] = useState(undefined);
    const [dynamicFormCompleted, setDynamicFormCompleted] = useState(false);

    useEffect(() => {
        checkDynamicForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkDynamicForm = () => {
        const _path = getCollectionPath(client, 'dynamic_forms');
        const _query = query(
            collection(firestore, _path),
            where('active', '==', true),
            where('type', '==', 'pre-checkout')
        );
        getDocs(_query)
            .then(snapshot => {
                const _form = !snapshot.empty ? snapshot.docs[0].data() : undefined;
                setDynamicForm(_form);
                setIsLoadingDynamicForm(false);
                if (!_form) startCheckout(undefined);
            })
            .catch(error => {
                Debug.printToLog('error', error);
                setDynamicForm(undefined);
                setIsLoadingDynamicForm(false);
                startCheckout(undefined);
            });
    };

    const startCheckout = (metafields) => {
        WebServices.getClientShoppingCartUrl(client, store.id, shoppingCart, false, metafields)
            .then(response => {
                console.log("response", response);
                if (response && !response.error) {
                    const loaded = paymentMode === 'external-checkout' ? !!response.url
                        : paymentMode === 'show-print' ? !!(response.clientDocumentUrl || response.url)
                            : !!response.id;
                    shoppingCart.dbId = response.id;
                    setCheckoutUrl(response.url);
                    setClientDocumentUrl(response.clientDocumentUrl);
                    setOrderNumber(response.orderNumber);
                    setPaymentNumber(response.paymentNumber);
                    setCheckoutLoaded(loaded);
                } else {
                    const type = response && response.error && response.error.type ? response.error.type : undefined;
                    let message;
                    let actionLabel;
                    let action;
                    switch (type) {
                        case 'NO_STOCK':
                            message = 'Algunos de los productos en el carro de compras no tienen stock disponible. Puede cambiarlos o eliminarlos del carro.';
                            actionLabel = 'Ir al carro';
                            action = () => handleChangeProducts();
                            setError({ message, actionLabel, action });
                            break;
                        default:
                            if (type) {
                                message = response.error.message;
                                actionLabel = "Cerrar";
                                action = () => handleClose('checkout-error-close');
                                setError({ message, actionLabel, action });
                                break;
                            } else {
                                setError(undefined);
                                break;
                            }
                    }
                    setCheckoutUrl(undefined);
                    setClientDocumentUrl(undefined);
                    setCheckoutLoaded(false);
                }
                setIsLoadingCheckout(false);
            });
    };

    const handleDynamicFormFinished = (values) => {
        setDynamicFormCompleted(true);
        startCheckout(values);
    };

    const isLoading = () => {
        return isLoadingCheckout;
    };

    const dataLoaded = () => {
        return checkoutLoaded;
    };

    const showDynamicForm = () => {
        return !isLoadingDynamicForm && !!dynamicForm && !dynamicFormCompleted;
    };

    const showCheckoutStage = () => {
        return !isLoadingDynamicForm && (!dynamicForm || dynamicFormCompleted);
    };

    const handleChangeProducts = () => {
        Activity.log(metadata, 'checkout', 'no-stock-error', 'open-cart-button', true, shoppingCart.toActivityLog());
        if (openShoppingCart) openShoppingCart();
        silentClose();
    };

    const renderLoader = () => {
        return (
            <div className='payment-modal-loader'>
                <DataLoader message='Iniciando interfaz de pago...' />
            </div>
        );
    };

    const renderError = () => {
        let _error = error;
        if (!_error) {
            _error = {
                message: 'Se ha producido un error al iniciar el proceso de checkout',
                actionLabel: 'Cerrar',
                action: () => handleClose('checkout-error-close')
            }
        }
        return (
            <div className='payment-modal-error'>
                <DataError
                    message={_error.message}
                    actionLabel={_error.actionLabel}
                    action={_error.action}
                    actionButtonLabelColor={actionButtonLabelColor}
                    actionButtonBackgroundColor={actionButtonBackgroundColor}
                    actionButtonBorderColor={actionButtonBorderColor}
                    maxWidth={500}
                />
            </div>
        );
    };

    const renderFlow = () => {
        switch (paymentMode) {
            case 'external-checkout':
                return renderExternalCheckout();
            case 'integrated-pos':
                return renderIntegratedPOS();
            case 'budget-print':
                return renderBudgetPrint();
            case 'show-print':
                return renderShowPrint();
            default:
                return renderInvalidFlow();
        }
    };

    const renderDynamicForm = () => {
        return (
            <DynamicForm
                steps={dynamicForm.steps}
                actionButtonBackgroundColor={actionButtonBackgroundColor}
                actionButtonBorderColor={actionButtonBorderColor}
                actionButtonLabelColor={actionButtonLabelColor}
                cancelButtonBackgroundColor={cancelButtonBackgroundColor}
                cancelButtonBorderColor={cancelButtonBorderColor}
                cancelButtonLabelColor={cancelButtonLabelColor}
                backButtonBackgroundColor={backButtonBackgroundColor}
                backButtonBorderColor={backButtonBorderColor}
                backButtonLabelColor={backButtonLabelColor}
                customInputOutline={customInputOutline}
                customInputClearButtonBackgroundColor={customInputClearButtonBackgroundColor}
                customInputClearButtonLabelColor={customInputClearButtonLabelColor}
                onClose={silentClose}
                onFinished={handleDynamicFormFinished}
            />
        );
    };

    const renderExternalCheckout = () => {
        return (
            <ExternalCheckout
                checkoutUrl={checkoutUrl}
                shoppingCart={shoppingCart}
                smartphoneOptionImage={smartphoneOptionImage}
                kioskOptionImage={kioskOptionImage}
                smartphoneOptionQrScanImage={smartphoneOptionQrScanImage}
                actionButtonBackgroundColor={actionButtonBackgroundColor}
                actionButtonBorderColor={actionButtonBorderColor}
                actionButtonLabelColor={actionButtonLabelColor}
                cancelButtonBackgroundColor={cancelButtonBackgroundColor}
                cancelButtonBorderColor={cancelButtonBorderColor}
                cancelButtonLabelColor={cancelButtonLabelColor}
                backButtonBackgroundColor={backButtonBackgroundColor}
                backButtonBorderColor={backButtonBorderColor}
                backButtonLabelColor={backButtonLabelColor}
                resetIdleModeManagerTimer={resetIdleModeManagerTimer}
                onClose={silentClose}
                metadata={metadata}
            />
        );
    };

    const renderIntegratedPOS = () => {
        return (
            <IntegratedPOS
                instance={instance}
                client={client}
                store={store}
                shoppingCart={shoppingCart}
                orderNumber={orderNumber}
                paymentNumber={paymentNumber}
                pk={pk}
                taxMultiplier={taxMultiplier}
                taxLabel={taxLabel}
                mainClientLogo={mainClientLogo}
                personOptionImage={personOptionImage}
                companyOptionImage={companyOptionImage}
                cardOptionImage={cardOptionImage}
                otherPaymentOptionImage={otherPaymentOptionImage}
                ticketOptionImage={ticketOptionImage}
                invoiceOptionImage={invoiceOptionImage}
                timerImage={timerImage}
                printMessageImage={printMessageImage}
                paymentSuccessImage={paymentSuccessImage}
                getHelpErrorImage={getHelpErrorImage}
                actionButtonBackgroundColor={actionButtonBackgroundColor}
                actionButtonBorderColor={actionButtonBorderColor}
                actionButtonLabelColor={actionButtonLabelColor}
                cancelButtonBackgroundColor={cancelButtonBackgroundColor}
                cancelButtonBorderColor={cancelButtonBorderColor}
                cancelButtonLabelColor={cancelButtonLabelColor}
                backButtonBackgroundColor={backButtonBackgroundColor}
                backButtonBorderColor={backButtonBorderColor}
                backButtonLabelColor={backButtonLabelColor}
                customInputOutline={customInputOutline}
                customInputClearButtonBackgroundColor={customInputClearButtonBackgroundColor}
                customInputClearButtonLabelColor={customInputClearButtonLabelColor}
                inSmallMode={inSmallMode}
                onClose={silentClose}
                allowClosing={allowClosing}
                preventClosing={preventClosing}
                flowFinished={flowFinished}
                openShoppingCart={openShoppingCart}
                metadata={metadata}
            />
        );
    };

    const renderBudgetPrint = () => {
        return (
            <Signup
                flow='budget-print'
                initialData={undefined}
                instance={instance}
                store={store}
                shoppingCart={shoppingCart}
                orderNumber={orderNumber}
                allowBudgetPrint={true}
                pk={pk}
                taxMultiplier={taxMultiplier}
                taxLabel={taxLabel}
                mainClientLogo={mainClientLogo}
                personOptionImage={personOptionImage}
                companyOptionImage={companyOptionImage}
                printMessageImage={printMessageImage}
                actionButtonBackgroundColor={actionButtonBackgroundColor}
                actionButtonBorderColor={actionButtonBorderColor}
                actionButtonLabelColor={actionButtonLabelColor}
                cancelButtonBackgroundColor={cancelButtonBackgroundColor}
                cancelButtonBorderColor={cancelButtonBorderColor}
                cancelButtonLabelColor={cancelButtonLabelColor}
                backButtonBackgroundColor={backButtonBackgroundColor}
                backButtonBorderColor={backButtonBorderColor}
                backButtonLabelColor={backButtonLabelColor}
                customInputOutline={customInputOutline}
                customInputClearButtonBackgroundColor={customInputClearButtonBackgroundColor}
                customInputClearButtonLabelColor={customInputClearButtonLabelColor}
                inSmallMode={inSmallMode}
                onClose={silentClose}
                onRegisterFinished={undefined}
                flowFinished={flowFinished}
                metadata={metadata}
            />
        );
    };

    const renderShowPrint = () => {
        return (
            <ShowPrint
                checkoutUrl={checkoutUrl}
                clientDocumentUrl={clientDocumentUrl}
                printMessageImage={printMessageImage}
                actionButtonBackgroundColor={actionButtonBackgroundColor}
                actionButtonBorderColor={actionButtonBorderColor}
                actionButtonLabelColor={actionButtonLabelColor}
                cancelButtonBackgroundColor={cancelButtonBackgroundColor}
                cancelButtonBorderColor={cancelButtonBorderColor}
                cancelButtonLabelColor={cancelButtonLabelColor}
                onClose={silentClose}
                flowFinished={flowFinished}
                metadata={metadata}
            />
        );
    };

    const renderInvalidFlow = () => {
        return (
            <div className='payment-modal-error'>
                <DataError
                    message='Flujo de pago inválido. Por favor, contacte a soporte técnico.'
                    actionLabel='Cerrar'
                    action={() => handleClose('invalid-flow-close')}
                    actionButtonLabelColor={actionButtonLabelColor}
                    actionButtonBackgroundColor={actionButtonBackgroundColor}
                    actionButtonBorderColor={actionButtonBorderColor}
                />
            </div>
        );
    };

    return (
        <React.Fragment>
            {isLoadingDynamicForm && renderLoader()}
            {showDynamicForm() && renderDynamicForm()}
            {showCheckoutStage() && isLoading() && renderLoader()}
            {showCheckoutStage() && !isLoading() && !dataLoaded() && renderError()}
            {showCheckoutStage() && !isLoading() && dataLoaded() && renderFlow()}
        </React.Fragment>
    );
}

export default PaymentModal;